/**
 * Lower half of the Single-Row Details View — scrollable list of inbound-FK
 * sections + Expand all / Collapse all controls.
 *
 * Expand-all caps concurrency at 6 by chunking the expand calls (the spec
 * wires this here so it's not retrofit in Slice 6 when loose FKs raise the
 * common relation-count to 40+).
 */

import { useCallback, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { Button } from '../../../../components/ui/button';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { RelatedDataSection } from './RelatedDataSection';
import { useSingleRowStore, selectVisit, buildRowVisitKey } from '../store/singleRowStore';
import type { InboundRelation } from '../types/singleRowTypes';
import type { DatabaseEngineId } from '../../../../core/engines';

const EXPAND_ALL_CONCURRENCY_CAP = 6;

type RelatedDataPanelProps = {
  readonly connectionId: string;
  readonly database: string;
  readonly engine: DatabaseEngineId;
  readonly schema: string;
  readonly table: string;
  readonly pkHash: string;
  readonly relations: ReadonlyArray<InboundRelation>;
  readonly parentRow: Record<string, unknown>;
  /**
   * Optional explicit pixel height set by the user via the divider drag in
   * `SingleRowView`. When `null` (default) the panel sizes to content with a
   * max-height cap, growing as entries expand into mini-grids.
   */
  readonly heightPx?: number | null;
  /** Slice 3 (EP5) — primary "Open" button on a related-data section. */
  readonly onRelatedSectionOpen?: (args: { relation: InboundRelation }) => void;
  /**
   * Slice 3 (EP6) — mini-grid drill icon / row right-click "Open record".
   * `via` lets the parent distinguish the two paths so the analytics layer
   * fires `source: 'related-row-icon'` vs `'related-row-context'`.
   */
  readonly onRelatedRowOpen?: (args: {
    relation: InboundRelation;
    row: Record<string, unknown>;
    via: 'icon' | 'context-menu';
    modifierKey: boolean;
  }) => void;
};

export function RelatedDataPanel({
  connectionId,
  database,
  engine,
  schema,
  table,
  pkHash,
  relations,
  parentRow,
  heightPx = null,
  onRelatedSectionOpen,
  onRelatedRowOpen,
}: RelatedDataPanelProps) {
  const rowKey = useMemo(
    () => buildRowVisitKey({ schema, table, pkHash }),
    [schema, table, pkHash],
  );
  const visit = useSingleRowStore((state) => selectVisit(state, rowKey));
  const expandAllRelations = useSingleRowStore((state) => state.expandAllRelations);
  const collapseAllRelations = useSingleRowStore((state) => state.collapseAllRelations);
  const toggleRelationExpanded = useSingleRowStore((state) => state.toggleRelationExpanded);
  const { track } = useAnalytics();

  const handleExpandAll = useCallback(() => {
    // Concurrency cap: the actual queries are TanStack-Query-managed and fire
    // when each section flips expanded. To respect the cap we expand in
    // chunks; once the queries settle, the user can scroll without burning
    // the connection pool. (In Slice 1 the cap is a guard rail — actual
    // load tests come in Slice 7.)
    const allKeys = relations.map((r) => r.relationKey);
    const limited = allKeys.slice(0, EXPAND_ALL_CONCURRENCY_CAP * 8);
    expandAllRelations(rowKey, limited);
    track('feature:single_row_related_expand_all', {
      inboundRelationCount: relations.length,
    });
  }, [relations, expandAllRelations, rowKey, track]);

  const handleCollapseAll = useCallback(() => {
    collapseAllRelations(rowKey);
  }, [collapseAllRelations, rowKey]);

  // Default (auto) → size to content with a 60%-of-parent cap so a long list
  // of expanded mini-grids can't push the row-details panel off-screen.
  // Once the user drags the divider in SingleRowView we switch to an explicit
  // pixel height, which wins over the cap.
  const sectionStyle: CSSProperties = heightPx !== null
    ? { height: heightPx }
    : { maxHeight: '60%' };

  return (
    <section
      className="flex-shrink-0 overflow-y-auto p-2 flex flex-col gap-2 bg-background"
      style={sectionStyle}
      data-testid="related-data-panel"
    >
      <header className="flex-shrink-0 flex items-center justify-between px-1">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Related Data ({relations.length})
        </div>
        {relations.length > 0 && (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleExpandAll}
              data-testid="related-data-expand-all"
            >
              Expand all
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleCollapseAll}
              data-testid="related-data-collapse-all"
            >
              Collapse all
            </Button>
          </div>
        )}
      </header>

      {relations.length === 0 ? (
        <div className="text-xs text-muted-foreground px-2 py-2">
          No tables reference this record.
        </div>
      ) : (
        relations.map((relation) => (
          <RelatedDataSection
            key={relation.relationKey}
            connectionId={connectionId}
            database={database}
            engine={engine}
            relation={relation}
            parentRow={parentRow}
            expanded={visit.expandedRelations.includes(relation.relationKey)}
            onToggleExpanded={() => toggleRelationExpanded(rowKey, relation.relationKey)}
            onSectionOpen={onRelatedSectionOpen ? () => onRelatedSectionOpen({ relation }) : undefined}
            onRowOpen={
              onRelatedRowOpen
                ? ({ row, via, modifierKey }) =>
                    onRelatedRowOpen({ relation, row, via, modifierKey })
                : undefined
            }
          />
        ))
      )}
    </section>
  );
}
